import { useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/currencyUtil';
import { useElementWidth } from '../hooks/useElementWidth';
import { POS_CATEGORY_BAR_LIMIT } from '../constants/posConst';
import '../css/Pos.css';

/** The same validated teal the trend line uses — it is the same measure. */
const BAR_COLOR = '#0e9384';

const ROW_H = 15;
const BAR_H = 7;
const LABEL_W = 66;
const RADIUS = 3;
/** Characters of product name the label column fits before it truncates. */
const NAME_CHARS = 11;

/**
 * A category's top products as small horizontal bars.
 *
 * Sits inside the breakdown's category cell, beside the name, filling the
 * column the rowspan otherwise leaves empty. The table to its right already
 * gives the exact amounts; what it cannot show is the SHAPE of a category —
 * whether its revenue rests on one product or is spread evenly — and that is
 * what a reader scanning for "what actually sells here" is after.
 *
 * Renders nothing for a single-product category: one full-width bar next to
 * one row states nothing the row has not already said.
 *
 * Measured on revenue rather than units, to match the Amount column beside it
 * and the category subtotal above it. Bars against units would rank Water over
 * Gatorade while the figures next to them said the opposite.
 *
 * Drawn at the container's MEASURED width, like the trend line, so the bars
 * fill whatever the column gives them. A fixed viewBox scaled to fit would
 * take the row height and the 8.5px labels up with it, and the chart would
 * grow taller every time the table got wider.
 *
 * @param {object} props
 * @param {Array<{name: string, revenue: number, quantity: number}>} props.products
 *   the category's products, already sorted best-first by summarisePosSales
 * @param {string} [props.categoryName] - named in the chart's title
 * @param {string} [props.measure='amount'] - 'amount' or 'quantity'; what the
 *   bar lengths encode, following the chart's own toggle
 * @param {number} [props.limit=POS_CATEGORY_BAR_LIMIT] - how many to draw
 * @returns {JSX.Element|null}
 */
function PosCategoryBars({ products, categoryName = '', measure = 'amount', limit = POS_CATEGORY_BAR_LIMIT }) {
  const { config } = useConfig();
  const [wrapRef, width] = useElementWidth(190);
  // Defined per component rather than at module scope: the symbol comes from
  // Config -> System, so it needs `config` in hand.
  const peso = (value) => formatMoney(value, config);

  if (!products || products.length < 2) return null;

  const byQuantity = measure === 'quantity';
  /**
   * What a bar's length encodes, following the chart's Amount/Quantity
   * toggle. Bars against revenue while the line plots units would rank the
   * same products in opposite orders on one screen.
   *
   * @param {{revenue: number, quantity: number}} row
   * @returns {number}
   */
  const valueOf = (row) => (byQuantity ? Number(row.quantity) || 0 : Number(row.revenue) || 0);

  const rows = products.slice(0, limit);
  const max = Math.max(...rows.map(valueOf), 1);
  const height = rows.length * ROW_H;
  // Floored so a squeezed column still draws a readable track rather than a
  // negative-width rect. The 4px keeps the last bar off the column's edge.
  const track = Math.max(40, width - LABEL_W - 4);

  /**
   * A bar rounded at its DATA END only, square against the baseline.
   *
   * A rect with rx rounds all four corners, which softens the baseline the
   * bars are measured from and makes short bars read as lozenges. This keeps
   * the left edge sharp so every bar starts from the same hard line.
   *
   * @param {number} x @param {number} y @param {number} w @param {number} h
   * @returns {string} an SVG path
   */
  const barPath = (x, y, w, h) => {
    const r = Math.min(RADIUS, w);
    return `M${x},${y} H${x + w - r} A${r},${r} 0 0 1 ${x + w},${y + r}`
      + ` V${y + h - r} A${r},${r} 0 0 1 ${x + w - r},${y + h} H${x} Z`;
  };

  return (
    <div className="pos-catbars-block">
      {/* Says what the bars are counting and for which category. Without it
          the chart is a row of unlabelled lengths sitting next to a table
          that is already full of numbers. The count is the ACTUAL number of
          bars, so a three-product category does not promise five. */}
      <p className="pos-catbars-title">
        Top {rows.length} product{rows.length === 1 ? '' : 's'}
        {categoryName ? ` for ${categoryName}` : ''}
      </p>

      {/* The height is set here rather than in CSS because only this component
          knows the row count. The SVG inside is taken OUT OF FLOW (see
          .pos-catbars) so its width can never feed back into the table column
          that is being measured to size it — left in flow, each measurement
          widened the cell, which widened the next measurement, and the chart
          ran away across the screen. */}
      <div ref={wrapRef} className="pos-catbars-wrap" style={{ height }}>
        <svg
          className="pos-catbars"
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label={`Top ${rows.length} by ${byQuantity ? 'units sold' : 'amount'}: `
            + rows.map(r => `${r.name} ${byQuantity ? `${r.quantity} sold` : peso(r.revenue)}`).join(', ')}
        >
          {rows.map((row, i) => {
            const y = i * ROW_H;
                const barWidth = Math.max(2, (valueOf(row) / max) * track);
            return (
              <g key={row.name}>
                <text x={0} y={y + BAR_H + 1} className="pos-catbars-label">
                  {row.name.length > NAME_CHARS ? `${row.name.slice(0, NAME_CHARS - 1)}\u2026` : row.name}
                </text>
                {/* A faint track, so a short bar is read against a length
                    rather than against empty space — and so every row ends on
                    the same right edge as the column it sits in. */}
                <rect x={LABEL_W} y={y} width={track} height={BAR_H} rx={RADIUS} className="pos-chart-track" />
                <path d={barPath(LABEL_W, y, barWidth, BAR_H)} fill={BAR_COLOR}>
                  <title>{`${row.name}: ${peso(row.revenue)} \u00b7 ${row.quantity} sold`}</title>
                </path>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

export default PosCategoryBars;
